import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

/**
 * ManualPromoteButton — explicit user override of promote_gate.
 *
 * Backend contract: POST /api/videos/{id}/manual_promote
 *   body: { reason: str (4..300 chars), confirm: bool }
 *   - 400 if confirm != true / reason too short
 *   - 404 if doc missing
 *   - 409 if status not in {needs_human, failed}
 *   - 200 / VideoRecord on success (idempotent on already-ready)
 *
 * Spends ZERO. Killswitch-agnostic — does NOT trigger any pipeline run.
 *
 * Used inside the Library detail modal for status in {needs_human, failed}.
 */
export default function ManualPromoteButton({
  videoId,
  videoTitle,
  gateReason,
  onPromoted,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const reasonOk = reason.trim().length >= 4 && reason.trim().length <= 300;

  const onSubmit = async () => {
    setErr(null);
    if (!reasonOk) {
      setErr("Reason must be between 4 and 300 characters.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/videos/${videoId}/manual_promote`, {
        reason: reason.trim(),
        confirm: true,
      });
      setOpen(false);
      setLoading(false);
      setReason("");
      onPromoted?.(r.data);
    } catch (e) {
      setLoading(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 400) {
        msg =
          (typeof detail === "string" ? detail : null) ||
          "Bad request — reason or confirm flag rejected.";
      } else if (status === 404) {
        msg = "Video not found (refresh the page).";
      } else if (status === 409) {
        msg =
          (typeof detail === "string" ? detail : null) ||
          "Cannot promote from this status.";
      } else if (!status) {
        msg = `Network error: ${e?.message || "unknown"}`;
      } else {
        msg = `Server error ${status}: ${
          typeof detail === "string" ? detail : JSON.stringify(detail)
        }`;
      }
      setErr(msg);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        data-testid="detail-manual-promote"
        className={className}
        onClick={() => {
          setReason("");
          setErr(null);
          setOpen(true);
        }}
      >
        <ShieldCheck className="mr-2 h-4 w-4" /> Mark as live-ready
      </Button>

      <AlertDialog open={open} onOpenChange={(o) => !loading && setOpen(o)}>
        <AlertDialogContent data-testid="manual-promote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
              Mark as live-ready (manual override)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-stone-700">
                <p>
                  Mark{" "}
                  <b>&ldquo;{videoTitle || videoId.slice(0, 8)}&rdquo;</b>{" "}
                  as <b>ready</b> despite the quality gate failure?
                </p>
                {gateReason ? (
                  <div
                    data-testid="manual-promote-gate-reason"
                    className="rounded-md border border-stone-300 bg-stone-50 px-3 py-2 text-xs"
                  >
                    <div className="font-semibold text-stone-700">
                      Gate said:
                    </div>
                    <code className="break-all text-stone-600">
                      {gateReason}
                    </code>
                  </div>
                ) : null}
                <div
                  data-testid="manual-promote-warning"
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  <AlertTriangle className="mr-1 inline h-3 w-3" />
                  This bypasses the 3+ minute / quality verifier rule. The
                  video will be marked ready in the library and eligible for
                  publishing. Spends nothing.
                </div>
                <label className="block text-xs font-medium text-stone-700">
                  Reason for override{" "}
                  <span className="text-stone-500">(4-300 chars)</span>
                </label>
                <textarea
                  data-testid="manual-promote-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={300}
                  placeholder="e.g. Reviewed manually — scene 7 carrot-count is correct, verifier got it wrong."
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <div className="text-right text-[11px] text-stone-500">
                  {reason.trim().length}/300
                </div>
                {err ? (
                  <div
                    data-testid="manual-promote-error"
                    className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900"
                  >
                    {err}
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="manual-promote-confirm"
              disabled={loading || !reasonOk}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Promoting…
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-3 w-3" />
                  Mark as ready
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
